import type { MatchState } from '../sim/state';
import type { ActionKind, MatchPhase } from '../types';
import { ProtocolError } from '../errors';
import { ACTIONS } from './codecs';
import {
  QUANT,
  quantU16,
  quantU8,
  quantI8,
  quantI16,
  quantFacing,
  dequantU16,
  dequantU8,
  dequantI8,
  dequantI16,
  dequantFacing,
} from './quantize';

// Snapshot delta codec (§9.3). Header 17 B + a 6 B match header (D62); per-changed-player field-masked deltas; ball/ref
// all-or-nothing (bits 30/31). Quantizers saturate. Delta vs a baseline (the client's acked
// snapshot); full when baselineTick = 0xFFFFFFFF. The decoder yields the QUANTIZED view (T-PROTO-011).

const FULL = 0xffffffff;
export const SNAPSHOT_HEADER_SIZE = 23; // 0x82 + tick + baselineTick + ackedInputTick + changedMask (17)
//                                        + D62 match header: clockTicks u32 + phase u8 + half/side u8 (6)

const F_POS = 1 << 0;
const F_VEL = 1 << 1;
const F_FACING = 1 << 2;
const F_ACTION = 1 << 3;
const F_STAMINA = 1 << 4;
const F_FLAGS = 1 << 5;
const F_Z = 1 << 6;
const F_SUSPEND = 1 << 7; // D65c: the red-card return deadline (u32). Set only while suspended ⇒ zero cost otherwise.

const BALL_BIT = 1 << 30;
const REF_BIT = 1 << 31;

export interface PlayerSnap {
  id: number;
  posX: number;
  posY: number;
  velX: number;
  velY: number;
  facing: number;
  action: number;
  actionTicks: number;
  stamina: number;
  flags: number;
  zq: number; // 0 = grounded (Z omitted on the wire)
  suspendUntil: number; // D65c: red-card return deadline (tick); 0 = not suspended. Drives the HUD countdown.
}
export interface BallSnap {
  posX: number;
  posY: number;
  zq: number;
  velX: number;
  velY: number;
  velZ: number;
  spinX: number;
  spinY: number;
  spinZ: number;
  possessor: number; // 255 = null
  gkLock: number; // 255 = null (D18)
  lastTouch: number; // 255 = null
}
export interface RefSnap {
  posX: number;
  posY: number;
  viewDir: number;
  flags: number;
}
/** D62: phase codes on the snapshot wire — APPEND ONLY (index-stable, mirrors the state codec's list). */
export const SNAP_PHASES: MatchPhase[] = ['LOBBY', 'KICKOFF', 'PLAY', 'RESTART_PENDING', 'GOAL_CELEBRATION', 'HALFTIME', 'FULLTIME'];

export interface DecodedSnapshot {
  tick: number;
  baselineTick: number;
  ackedInputTick: number;
  players: PlayerSnap[];
  ball: BallSnap;
  ref: RefSnap;
  /** D62 match header — the authoritative match clock/phase/half/side, carried on EVERY snapshot.
   *  Before this the client derived its HUD clock from the local render timeline and its phase from
   *  reliable events only, so a dead stream rendered a frozen world under a running clock, a resume
   *  showed the wrong phase, and prediction never learned about the half-time side swap. */
  clockTicks: number;
  phase: MatchPhase;
  half: 1 | 2 | 3;
  /** attackDir[0]; attackDir[1] is always its negation (flipped together at HALF_END). */
  attackDir0: 1 | -1;
}

function playerFlags(sentOff: boolean, isGk: boolean, shouting: boolean, hasBall: boolean, card: number, calling: boolean): number {
  return (sentOff ? 1 : 0) | (isGk ? 2 : 0) | (shouting ? 4 : 0) | (hasBall ? 8 : 0) | ((card & 3) << 4) | (calling ? 64 : 0); // D42: bit 64 = calling
}

/** The full quantized view of a state (T-PROTO-011 reference). tick/acked default to state.tick. */
export function quantizeSnapshot(state: MatchState, tick = state.tick, ackedInputTick = state.tick): DecodedSnapshot {
  const players: PlayerSnap[] = state.players.map((p) => ({
    id: p.id,
    posX: quantU16(p.pos.x, QUANT.POS_X.min, QUANT.POS_X.max),
    posY: quantU16(p.pos.y, QUANT.POS_Y.min, QUANT.POS_Y.max),
    velX: quantI8(p.vel.x, QUANT.VEL_LSB),
    velY: quantI8(p.vel.y, QUANT.VEL_LSB),
    facing: quantFacing(p.facing),
    action: ACTIONS.indexOf(p.action),
    actionTicks: Math.max(0, Math.min(255, p.actionTicks)),
    stamina: quantU8(p.stamina, 0, 1),
    flags: playerFlags(p.sentOff, p.isGk, p.shouting, state.ball.possessor === p.id, p.card === null ? 0 : p.card === 'YELLOW' ? 1 : 2, p.calling),
    zq: Math.max(0, Math.min(255, Math.round(p.z / QUANT.Z_LSB))),
    suspendUntil: p.suspendedUntilTick, // D65c
  }));
  const b = state.ball;
  const ball: BallSnap = {
    posX: quantU16(b.pos.x, QUANT.POS_X.min, QUANT.POS_X.max),
    posY: quantU16(b.pos.y, QUANT.POS_Y.min, QUANT.POS_Y.max),
    zq: quantU16(b.pos.z, QUANT.BALL_Z.min, QUANT.BALL_Z.max),
    velX: quantI16(b.vel.x, QUANT.BALL_VEL_LSB),
    velY: quantI16(b.vel.y, QUANT.BALL_VEL_LSB),
    velZ: quantI16(b.vel.z, QUANT.BALL_VEL_LSB),
    spinX: quantI8(Math.max(-QUANT.SPIN_MAX, Math.min(QUANT.SPIN_MAX, b.spin.x)), QUANT.SPIN_LSB),
    spinY: quantI8(Math.max(-QUANT.SPIN_MAX, Math.min(QUANT.SPIN_MAX, b.spin.y)), QUANT.SPIN_LSB),
    spinZ: quantI8(Math.max(-QUANT.SPIN_MAX, Math.min(QUANT.SPIN_MAX, b.spin.z)), QUANT.SPIN_LSB),
    possessor: b.possessor ?? 255,
    gkLock: b.gkLock ?? 255,
    lastTouch: b.lastTouch?.entity ?? 255,
  };
  const r = state.referee;
  const ref: RefSnap = {
    posX: quantU16(r.pos.x, QUANT.POS_X.min, QUANT.POS_X.max),
    posY: quantU16(r.pos.y, QUANT.POS_Y.min, QUANT.POS_Y.max),
    viewDir: quantFacing(r.viewDir),
    flags: 0,
  };
  return {
    tick,
    baselineTick: FULL,
    ackedInputTick,
    players,
    ball,
    ref,
    clockTicks: state.clockTicks,
    phase: state.phase,
    half: state.half,
    attackDir0: state.attackDir[0],
  };
}

class W {
  private view = new DataView(new ArrayBuffer(4096));
  private off = 0;
  u8(v: number): void {
    this.view.setUint8(this.off, v & 0xff);
    this.off += 1;
  }
  u16(v: number): void {
    this.view.setUint16(this.off, v & 0xffff, true);
    this.off += 2;
  }
  i16(v: number): void {
    this.view.setInt16(this.off, v, true);
    this.off += 2;
  }
  i8(v: number): void {
    this.view.setInt8(this.off, v);
    this.off += 1;
  }
  u32(v: number): void {
    this.view.setUint32(this.off, v >>> 0, true);
    this.off += 4;
  }
  bytes(): Uint8Array {
    return new Uint8Array(this.view.buffer.slice(0, this.off));
  }
}

/** Encode a snapshot for `state`, delta vs `baseline` (null ⇒ full). */
export function encodeSnapshot(
  state: MatchState,
  baseline: DecodedSnapshot | null,
  tick: number,
  ackedInputTick: number,
): Uint8Array {
  const cur = quantizeSnapshot(state, tick, ackedInputTick);
  const full = baseline === null;
  const baseP = new Map<number, PlayerSnap>();
  if (baseline) for (const p of baseline.players) baseP.set(p.id, p);

  let changedMask = 0;
  const parts: Array<{ ps: PlayerSnap; fieldMask: number }> = [];
  for (const ps of cur.players) {
    const bp = baseP.get(ps.id);
    let fieldMask = 0;
    if (full || !bp) {
      fieldMask = F_POS | F_VEL | F_FACING | F_ACTION | F_STAMINA | F_FLAGS | (ps.zq > 0 ? F_Z : 0) | (ps.suspendUntil > 0 ? F_SUSPEND : 0);
    } else {
      if (ps.posX !== bp.posX || ps.posY !== bp.posY) fieldMask |= F_POS;
      if (ps.velX !== bp.velX || ps.velY !== bp.velY) fieldMask |= F_VEL;
      if (ps.facing !== bp.facing) fieldMask |= F_FACING;
      if (ps.action !== bp.action || ps.actionTicks !== bp.actionTicks) fieldMask |= F_ACTION;
      if (ps.stamina !== bp.stamina) fieldMask |= F_STAMINA;
      if (ps.flags !== bp.flags) fieldMask |= F_FLAGS;
      if (ps.zq > 0) fieldMask |= F_Z;
      if (ps.suspendUntil !== bp.suspendUntil) fieldMask |= F_SUSPEND; // D65c: delta-friendly — constant while suspended
    }
    const zChanged = !full && bp ? ps.zq !== bp.zq : ps.zq > 0;
    if (fieldMask !== 0 || zChanged) {
      changedMask |= 1 << ps.id;
      parts.push({ ps, fieldMask });
    }
  }
  const ballChanged = full || !baseline ? true : ballDiffers(cur.ball, baseline.ball);
  const refChanged = full || !baseline ? true : refDiffers(cur.ref, baseline.ref);
  if (ballChanged) changedMask |= BALL_BIT;
  if (refChanged) changedMask |= REF_BIT;

  const w = new W();
  w.u8(0x82);
  w.u32(tick);
  w.u32(full ? FULL : baseline!.tick);
  w.u32(ackedInputTick);
  w.u32(changedMask);
  // D62 match header (6 B, never delta'd — a resuming or stalled client must ALWAYS learn the truth)
  w.u32(state.clockTicks);
  w.u8(Math.max(0, SNAP_PHASES.indexOf(state.phase)));
  w.u8((state.half & 0x03) | (state.attackDir[0] > 0 ? 0x80 : 0));
  for (const { ps, fieldMask } of parts) {
    w.u8(ps.id);
    w.u8(fieldMask);
    if (fieldMask & F_POS) {
      w.u16(ps.posX);
      w.u16(ps.posY);
    }
    if (fieldMask & F_VEL) {
      w.i8(ps.velX);
      w.i8(ps.velY);
    }
    if (fieldMask & F_FACING) w.u8(ps.facing);
    if (fieldMask & F_ACTION) {
      w.u8(ps.action);
      w.u8(ps.actionTicks);
    }
    if (fieldMask & F_STAMINA) w.u8(ps.stamina);
    if (fieldMask & F_FLAGS) w.u8(ps.flags);
    if (fieldMask & F_Z) w.u8(ps.zq);
    if (fieldMask & F_SUSPEND) w.u32(ps.suspendUntil >>> 0); // D65c
  }
  if (ballChanged) {
    const b = cur.ball;
    w.u16(b.posX);
    w.u16(b.posY);
    w.u16(b.zq);
    w.i16(b.velX);
    w.i16(b.velY);
    w.i16(b.velZ);
    w.i8(b.spinX);
    w.i8(b.spinY);
    w.i8(b.spinZ);
    w.u8(b.possessor);
  w.u8(b.gkLock);
    w.u8(b.lastTouch);
  }
  if (refChanged) {
    const r = cur.ref;
    w.u16(r.posX);
    w.u16(r.posY);
    w.u8(r.viewDir);
    w.u8(r.flags);
  }
  return w.bytes();
}

function ballDiffers(a: BallSnap, b: BallSnap): boolean {
  return (
    a.posX !== b.posX || a.posY !== b.posY || a.zq !== b.zq || a.velX !== b.velX || a.velY !== b.velY || a.velZ !== b.velZ ||
    a.spinX !== b.spinX || a.spinY !== b.spinY || a.spinZ !== b.spinZ || a.possessor !== b.possessor || a.gkLock !== b.gkLock || a.lastTouch !== b.lastTouch
  );
}
function refDiffers(a: RefSnap, b: RefSnap): boolean {
  return a.posX !== b.posX || a.posY !== b.posY || a.viewDir !== b.viewDir || a.flags !== b.flags;
}

class R {
  private off = 0;
  constructor(
    private readonly view: DataView,
    private readonly len: number,
  ) {}
  private need(n: number): void {
    if (this.off + n > this.len) throw new ProtocolError('decodeSnapshot: unexpected end of buffer');
  }
  u8(): number {
    this.need(1);
    return this.view.getUint8(this.off++);
  }
  u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.off, true);
    this.off += 2;
    return v;
  }
  i16(): number {
    this.need(2);
    const v = this.view.getInt16(this.off, true);
    this.off += 2;
    return v;
  }
  i8(): number {
    this.need(1);
    return this.view.getInt8(this.off++);
  }
  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.off, true);
    this.off += 4;
    return v;
  }
  get atEnd(): boolean {
    return this.off === this.len;
  }
}

/** Decode a snapshot, applying the delta over `baseline` (required unless the snapshot is full). */
export function decodeSnapshot(bytes: Uint8Array, baseline: DecodedSnapshot | null): DecodedSnapshot {
  const r = new R(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength), bytes.byteLength);
  if (r.u8() !== 0x82) throw new ProtocolError('decodeSnapshot: bad msg type');
  const tick = r.u32();
  const baselineTick = r.u32();
  const ackedInputTick = r.u32();
  const changedMask = r.u32();
  const clockTicks = r.u32(); // D62 match header
  const phase = SNAP_PHASES[r.u8()] ?? 'PLAY';
  const halfByte = r.u8();
  const half = ((halfByte & 0x03) || 1) as 1 | 2 | 3;
  const attackDir0: 1 | -1 = (halfByte & 0x80) !== 0 ? 1 : -1;
  const full = baselineTick === FULL;
  if (!full && !baseline) throw new ProtocolError('decodeSnapshot: delta needs a baseline');

  const players: PlayerSnap[] = [];
  const baseP = new Map<number, PlayerSnap>();
  if (baseline) for (const p of baseline.players) baseP.set(p.id, p);

  for (let id = 0; id < 22; id++) {
    if (changedMask & (1 << id)) {
      const pid = r.u8();
      if (pid !== id) throw new ProtocolError('decodeSnapshot: player id mismatch');
      const fieldMask = r.u8();
      const bp = baseP.get(id);
      const ps: PlayerSnap = bp
        ? { ...bp }
        : { id, posX: 0, posY: 0, velX: 0, velY: 0, facing: 0, action: 0, actionTicks: 0, stamina: 0, flags: 0, zq: 0, suspendUntil: 0 };
      ps.id = id;
      if (fieldMask & F_POS) {
        ps.posX = r.u16();
        ps.posY = r.u16();
      }
      if (fieldMask & F_VEL) {
        ps.velX = r.i8();
        ps.velY = r.i8();
      }
      if (fieldMask & F_FACING) ps.facing = r.u8();
      if (fieldMask & F_ACTION) {
        ps.action = r.u8();
        ps.actionTicks = r.u8();
      }
      if (fieldMask & F_STAMINA) ps.stamina = r.u8();
      if (fieldMask & F_FLAGS) ps.flags = r.u8();
      ps.zq = fieldMask & F_Z ? r.u8() : 0; // Z is stateless: omission ⇒ z=0 (T-PROTO-012)
      if (fieldMask & F_SUSPEND) ps.suspendUntil = r.u32(); // D65c: delta — omission inherits the baseline deadline
      players.push(ps);
    } else if (baseP.has(id)) {
      players.push({ ...baseP.get(id)! });
    }
  }

  let ball: BallSnap;
  if (changedMask & BALL_BIT) {
    ball = {
      posX: r.u16(),
      posY: r.u16(),
      zq: r.u16(),
      velX: r.i16(),
      velY: r.i16(),
      velZ: r.i16(),
      spinX: r.i8(),
      spinY: r.i8(),
      spinZ: r.i8(),
      possessor: r.u8(),
      gkLock: r.u8(),
      lastTouch: r.u8(),
    };
  } else if (baseline) {
    ball = { ...baseline.ball };
  } else {
    throw new ProtocolError('decodeSnapshot: full snapshot missing ball');
  }

  let ref: RefSnap;
  if (changedMask & REF_BIT) {
    ref = { posX: r.u16(), posY: r.u16(), viewDir: r.u8(), flags: r.u8() };
  } else if (baseline) {
    ref = { ...baseline.ref };
  } else {
    throw new ProtocolError('decodeSnapshot: full snapshot missing ref');
  }

  if (!r.atEnd) throw new ProtocolError('decodeSnapshot: trailing bytes');
  return { tick, baselineTick, ackedInputTick, players, ball, ref, clockTicks, phase, half, attackDir0 };
}

/** Read (tick, baselineTick) from a snapshot header without decoding — the client picks its baseline (§9.3). */
export function peekSnapshotTicks(bytes: Uint8Array): { tick: number; baselineTick: number } {
  if (bytes.length < 9) throw new ProtocolError('peekSnapshotTicks: too short');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== 0x82) throw new ProtocolError('peekSnapshotTicks: bad msg type');
  return { tick: v.getUint32(1, true), baselineTick: v.getUint32(5, true) };
}

export interface PlayerView {
  id: number;
  pos: { x: number; y: number };
  vel: { x: number; y: number };
  facing: number;
  z: number;
  stamina: number;
  action: ActionKind;
  actionTicks: number;
  flags: number;
}
export interface BallView {
  pos: { x: number; y: number; z: number };
  vel: { x: number; y: number; z: number };
  possessor: number; // 255 ⇒ null
}

/** Dequantize a decoded PlayerSnap into engine units (§9.3 inverse) — feeds prediction/interpolation. */
export function dequantPlayer(ps: PlayerSnap): PlayerView {
  return {
    id: ps.id,
    pos: { x: dequantU16(ps.posX, QUANT.POS_X.min, QUANT.POS_X.max), y: dequantU16(ps.posY, QUANT.POS_Y.min, QUANT.POS_Y.max) },
    vel: { x: dequantI8(ps.velX, QUANT.VEL_LSB), y: dequantI8(ps.velY, QUANT.VEL_LSB) },
    facing: dequantFacing(ps.facing),
    z: ps.zq * QUANT.Z_LSB,
    stamina: dequantU8(ps.stamina, 0, 1),
    action: ACTIONS[ps.action] ?? 'IDLE',
    actionTicks: ps.actionTicks,
    flags: ps.flags,
  };
}

/** Dequantize a decoded BallSnap into engine units. */
export function dequantBall(bs: BallSnap): BallView {
  return {
    pos: {
      x: dequantU16(bs.posX, QUANT.POS_X.min, QUANT.POS_X.max),
      y: dequantU16(bs.posY, QUANT.POS_Y.min, QUANT.POS_Y.max),
      z: dequantU16(bs.zq, QUANT.BALL_Z.min, QUANT.BALL_Z.max),
    },
    vel: { x: dequantI16(bs.velX, QUANT.BALL_VEL_LSB), y: dequantI16(bs.velY, QUANT.BALL_VEL_LSB), z: dequantI16(bs.velZ, QUANT.BALL_VEL_LSB) },
    possessor: bs.possessor,
  };
}
